import { useEffect } from "react";
import type { DocFontInfo } from "../lib/protocol.ts";

/** Fonts panel: what the document actually uses, whether each face
 *  travels with the file, and which pages need it. A non-embedded font
 *  is the usual reason a PDF looks different on someone else's
 *  machine, so it's called out. */
export function FontsPanel({
  fonts,
  onClose,
  paneWidth,
}: {
  fonts: DocFontInfo[] | null;
  onClose: () => void;
  paneWidth: number;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  const missing = fonts?.filter((f) => !f.embedded).length ?? 0;
  return (
    <div className="flex min-h-0 w-full flex-1 flex-col bg-[color:var(--color-bg)]" style={{ width: paneWidth }}>
      <div className="flex items-center gap-2 border-b border-[color:var(--color-border)] p-3">
        <i className="ri-font-family text-lg text-[color:var(--color-fg-muted)]" aria-hidden />
        <h2 className="text-sm font-semibold">Fonts</h2>
        <span className="text-xs text-[color:var(--color-fg-muted)]">{fonts?.length ?? "—"}</span>
        <button
          type="button"
          title="Close"
          aria-label="Close fonts"
          onClick={onClose}
          className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-bg-subtle)]"
        >
          <i className="ri-close-line" aria-hidden />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {!fonts && <p className="p-3 text-xs text-[color:var(--color-fg-muted)]">Reading fonts…</p>}
        {fonts?.length === 0 && (
          <p className="p-3 text-xs text-[color:var(--color-fg-muted)]">
            This document doesn't reference any fonts (it may be a pure scan — try OCR).
          </p>
        )}
        {fonts?.map((f) => (
          <div
            key={f.rawName + f.type}
            className="mb-1 rounded-md border border-[color:var(--color-border)] px-2 py-1.5"
          >
            <div className="flex items-center gap-1.5">
              <span className="min-w-0 flex-1 truncate text-xs font-medium" title={f.rawName}>
                {f.name}
              </span>
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] ${
                  f.embedded
                    ? "bg-[#2f9e44]/15 text-[#2f9e44]"
                    : "bg-[#e8590c]/15 text-[#e8590c]"
                }`}
                title={
                  f.embedded
                    ? "The font file is inside this PDF — it renders the same everywhere"
                    : "Not embedded — viewers substitute a lookalike, so spacing can shift"
                }
              >
                {f.embedded ? (f.subset ? "subset" : "embedded") : "not embedded"}
              </span>
            </div>
            <p className="mt-0.5 text-[11px] text-[color:var(--color-fg-muted)]">
              {f.type} · page{f.pages.length === 1 ? "" : "s"}{" "}
              {f.pages.length > 6
                ? `${f.pages.slice(0, 6).join(", ")}…`
                : f.pages.join(", ")}
            </p>
          </div>
        ))}
      </div>
      {missing > 0 && (
        <p className="border-t border-[color:var(--color-border)] p-2 text-[10px] text-[color:var(--color-fg-muted)]">
          {missing} font{missing === 1 ? " is" : "s are"} not embedded. Re-styling that text with a
          library font embeds it, guaranteeing it looks the same for everyone.
        </p>
      )}
    </div>
  );
}
